import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";
import { MiksiToimii } from "@/components/hub/Callouts";
import hero from "@/assets/hub/montessori-suomessa-2026.jpg";
import koulu from "@/assets/hub/montessori-suomessa-2026-koulu.jpg";
import koulutus from "@/assets/hub/montessori-suomessa-2026-koulutus.jpg";

const SLUG = "montessori-suomessa-2026";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori Suomessa 2026 — pedagogiikka, joka kiinnostaa yhä uusia perheitä";
const DESC =
  "Montessori on Suomessa päiväkoteja, kunnallista perusopetusta, yläkoulua ja kansainvälistä opettajankoulutusta. Katso, mitä vuonna 2026 tapahtuu Helsingissä, Espoossa, Salossa ja muualla.";
const PUBLISHED = "2026-08-23";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/montessori-suomessa-2026")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      slug={SLUG}
      eyebrow="Montessori Suomessa"
      title={TITLE}
      lead="Montessori ei ole Suomessa uusi ilmiö. Vuonna 2026 sen ympärillä tapahtuu kuitenkin paljon kiinnostavaa: kunnallista perusopetusta, yläkoulua, päiväkoteja ja kansainvälistä opettajankoulutusta."
      readingMinutes={12}
      publishedISO={PUBLISHED}
      updatedISO={PUBLISHED}
      heroImage={hero}
      heroAlt="Vaalea, skandinaavinen Montessori-luokkahuone ikkunanvalossa"
    >
      <h2>Montessori on Suomessa oikeaa varhaiskasvatusta, perusopetusta ja opettajankoulutusta</h2>
      <p>
        Kun Montessori joskus mielletään lähinnä puisiin leluihin, kauniisiin lastenhuoneisiin tai sosiaalisessa mediassa näkyviin aktiviteetteihin, vuoden 2026 tapahtumat kertovat aivan toisenlaisen tarinan.
      </p>
      <p>
        Suomessa Montessori on oikeaa varhaiskasvatusta, perusopetusta ja opettajankoulutusta. Se näkyy Helsingin Montessori-koulun infoiltana, Espoon kaupungin kouluhakuna, Salossa ja Halikossa järjestettyinä avoimien ovien päivinä sekä uusina AMI-opettajankoulutuksina pääkaupunkiseudulla.
      </p>

      <h2>7.1.2026 — vanhemmat tutustuivat Helsingin Montessori-kouluun</h2>
      <p>
        Vuosi alkoi Helsingissä konkreettisesti. Helsingin Montessori-koulu järjesti 7. tammikuuta Teams-infon Montessori-koulusta kiinnostuneille huoltajille. Tilaisuudessa esiteltiin Montessori-menetelmää alakoulussa, koulun toimintaa ja annettiin vanhemmille mahdollisuus esittää kysymyksiä.
      </p>
      <p>
        Helsingin Montessori-koulu on Suomen ensimmäinen kokonaan Montessori-pedagogiikkaan perustuva peruskoulu. Lukuvuonna 2026–2027 koulussa on 121 oppilasta vuosiluokilla 1–6. Koulu noudattaa suomalaista valtakunnallista opetussuunnitelmaa, mutta opetuksessa käytetään kansainvälisiä Montessori-menetelmiä ja -välineitä.
      </p>
      <MiksiToimii>
        Montessori ei tarkoita suomalaisesta koulujärjestelmästä irtautumista. Lapsi opiskelee samoja keskeisiä asioita kuin muutkin suomalaiset koululaiset, mutta oppiminen tapahtuu itsenäisen työskentelyn, konkreettisten materiaalien ja pitkäkestoisen keskittymisen kautta.
      </MiksiToimii>
      <p>
        Koulun sivuilla on jo ilmoitettu, että seuraavia Montessori-koulusta kiinnostuneille perheille tarkoitettuja infoiltoja järjestetään jälleen marras–joulukuussa 2026 ennen lukuvuoden 2027–2028 hakua. Tarkat päivämäärät julkaistaan myöhemmin.
      </p>

      <figure className="not-prose my-10">
        <img
          src={koulu}
          alt="Skandinaavinen koulukäytävä luokkamerkinnöin"
          width={1280}
          height={720}
          loading="lazy"
          className="aspect-[16/9] w-full rounded-3xl border border-border object-cover"
        />
        <figcaption className="mt-2 text-center text-xs text-muted-foreground">
          Montessori-koulu voi näyttää tavalliselta koululta — eron tekee pedagogiikka, ei arkkitehtuuri.
        </figcaption>
      </figure>

      <h2>Tammikuu 2026 — Montessori oli mukana Espoon kaupungin kouluhaussa</h2>
      <p>
        Espoon kaupunki tarjoaa Montessori-opetusta 1.–6.-luokkalaisille Niittykummun ja Ruusutorpan kouluissa. Lukuvuoden 2026–2027 ensimmäisen luokan Montessori-opetukseen haettiin 8.–20. tammikuuta 2026 aivan kaupungin oman Wilma-järjestelmän kautta. Molemmissa kouluissa oli kahdeksan ensimmäisen luokan Montessori-paikkaa, eikä valintaan kuulunut pääsykoetta.
      </p>
      <p>
        Espoo kuvaa Montessori-opetusta menetelmäksi, jossa toiminnalliset materiaalit mahdollistavat yksilöllisen ja kiireettömän etenemisen. Tavoitteena on vahvistaa yhteisöllisyyttä, itsenäistä työskentelyä ja lapsen vastuuta omasta oppimisestaan.
      </p>
      <p>
        Tämä tekee Espoon mallista erityisen kiinnostavan: Montessori ei toimi siellä erillisenä vaihtoehtokouluna, vaan osana kunnallista suomalaista peruskoulua. Se kertoo, kuinka Montessori-ajatuksia voidaan yhdistää suomalaiseen koulutusjärjestelmään ilman, että niiden tarvitsee olla kaksi vastakkaista maailmaa.
      </p>

      <h2>Tammikuu 2026 — Montessori jatkuu Suomessa jopa yläkouluun</h2>
      <p>
        Moni tietää Montessori-päiväkodeista. Paljon harvempi tietää, että Suomessa Montessori-opetus voi jatkua myös teini-ikään. Helsingin Apollon Yhteiskoulussa toimii Suomen Montessoriliiton mukaan Suomen ainoa 7.–9.-luokkien Montessori-linja.
      </p>
      <p>
        Lukuvuotta 2026 koskeva hakuaika oli 2.–16. tammikuuta 2026. Oppilaita voidaan ottaa myös koulun oman oppilaaksiottoalueen ulkopuolelta, eikä aikaisempaa Montessori-taustaa vaadita. Yläkoulun Montessori-opiskelua kuvataan yhteisölliseksi, monialaiseksi ja vahvasti eriytetyksi. Oppilaita ohjaavat sekä AMI-koulutettu Montessori-opettaja että aineenopettajat.
      </p>
      <p>
        Tämä rikkoo yhtä yleistä mielikuvaa: Montessori ei lopu siihen, kun lapsi oppii lukemaan. Pedagogiikan tavoitteet — itsenäisyys, vastuu omasta työstä, keskittyminen ja kokonaisuuksien ymmärtäminen — voivat olla jopa entistä kiinnostavampia lapsen kasvaessa.
      </p>

      <h2>14.3.2026 — Montessori-päiväkodit avasivat ovensa Salossa ja Halikossa</h2>
      <p>
        Kaikki Montessoriin tutustuminen ei tapahdu verkkosivuilla tai hakulomakkeilla. Lauantaina 14. maaliskuuta 2026 klo 9–12 Montessori-päiväkoti Aurinkoleijona järjesti avoimet ovet sekä Salon että Halikon yksiköissään. Samalla perheet pystyivät tutustumaan syksyn 2026 vapaisiin paikkoihin.
      </p>
      <p>
        Aurinkoleijona on tarjonnut Montessori-painotteista varhaiskasvatusta jo vuodesta 1998 ja tarjoaa toimintaa noin yhdeksän kuukauden ikäisistä lapsista esiopetusikäisiin saakka. Avoimet ovet ovat Montessori-pedagogiikan kohdalla erityisen hyödylliset: Montessoria on vaikea ymmärtää kokonaan vain lukemalla siitä.
      </p>
      <p>
        Kun astuu oikeaan Montessori-ympäristöön, alkaa huomata yksityiskohtia: materiaalit ovat lapsen ulottuvilla, tavaroilla on omat paikkansa, kalusteet ovat lapsen kokoisia ja lapsella on mahdollisuus valita tekemistään. Ehkä tärkeimpänä — ympäristön on tarkoitus auttaa lasta toimimaan mahdollisimman paljon itse.
      </p>

      <h2>Vuosi 2026 on merkittävä myös uusien Montessori-opettajien koulutuksessa</h2>
      <p>
        Montessori-toiminta ei voi Suomessa kasvaa vain lisäämällä päiväkotien tai luokkien määrää. Tarvitaan myös koulutettuja aikuisia. Siksi ehkä yksi vuoden 2026 tärkeimmistä Montessori-ilmiöistä tapahtuu paljon hiljaisemmin.
      </p>
      <p>
        Suomen Montessori-koulutuksen AMI 3–6 -opettajakoulutus jatkui Helsingissä vuoden 2026 aikana. Pakilan Montessori-leikkikoulussa järjestettiin lähiopetusmoduuli 3.–6. tammikuuta ja 10. tammikuuta, toinen jakso 16.–20. helmikuuta sekä pitkä viimeinen koulutusjakso 1. kesäkuuta–11. heinäkuuta 2026. Viimeiseen jaksoon sisältyivät kirjalliset ja suulliset loppukokeet.
      </p>
      <p>
        Kyseessä ei ole lyhyt verkkokurssi. AMI eli Association Montessori Internationale on Maria Montessorin perustama kansainvälinen Montessori-organisaatio. Suomessa järjestettävässä 3–6-vuotiaiden koulutuksessa opiskelijat käyvät läpi muun muassa Montessori-filosofiaa, lapsen kehitystä, käytännön elämän töitä, aistimateriaaleja, kieltä, matematiikkaa, luonnontieteitä sekä laajoja käytännön materiaaliharjoituksia. Koulutukseen kuuluu myös lapsihavainnointia ja työskentelyä Montessori-ryhmissä. Hyväksytysti valmistuvat saavat kansainvälisen AMI-diplomin.
      </p>

      <figure className="not-prose my-10">
        <img
          src={koulutus}
          alt="Montessori-opettajankoulutuksen materiaaleja ja muistiinpanoja"
          width={1280}
          height={720}
          loading="lazy"
          className="aspect-[16/9] w-full rounded-3xl border border-border object-cover"
        />
        <figcaption className="mt-2 text-center text-xs text-muted-foreground">
          Koulutettuja opettajia tarvitaan, jotta Montessori säilyy pedagogiikkana eikä muutu pelkäksi välinekokoelmaksi.
        </figcaption>
      </figure>

      <h2>Samalla Suomessa koulutetaan Montessori-opettajia myös 6–12-vuotiaille</h2>
      <p>
        Myös alakouluikäisten Montessori-opettajien koulutus on ollut aktiivisesti käynnissä vuonna 2026. Suomen Montessori-instituutin 6–12-vuotiaiden AMI-koulutuksessa järjestettiin tammikuussa lähiopetusjakso, keväällä etäluentoja ja havainnointiharjoittelua sekä kesällä 2026 kaksi pitkää lähiopetusjaksoa: 1.–18. kesäkuuta ja 13.–31. heinäkuuta.
      </p>
      <p>
        Syksyllä ohjelma jatkuu etäopinnoilla ja opetusharjoittelulla. Lähiopetus järjestetään Espoon Ruusutorpan koulussa, jossa toimii myös Montessori-luokkia. Koulutus on perusteellinen: teoria- ja materiaaliharjoittelun lisäksi siihen kuuluu Montessori-luokkien havainnointia ja varsinaista opetusharjoittelua.
      </p>
      <MiksiToimii>
        Uusia Montessori-paikkoja voidaan perustaa, mutta ilman osaavia opettajia menetelmä muuttuu helposti vain kokoelmaksi kauniita välineitä. Montessori ei ole väline. Se on tapa nähdä lapsi.
      </MiksiToimii>

      <h2>Mitä vuoden 2026 tapahtumat kertovat Suomesta?</h2>
      <p>
        Kun nämä tapahtumat laittaa rinnakkain, muodostuu kiinnostava kuva. Yhdessä kaupungissa päiväkoti kutsuu perheitä avoimiin oviin, toisessa kaupungissa Montessoriin haetaan kunnallisen Wilman kautta. Helsingissä toimii kokonainen Montessori-peruskoulu, yläkouluikäisille on oma Montessori-vaihtoehto, ja samaan aikaan Suomessa koulutetaan uusia kansainvälisesti päteviä Montessori-opettajia.
      </p>
      <p>
        Suomen Montessoriliitto listaa Montessori-varhaiskasvatusta esimerkiksi Espoossa, Helsingissä, Forssassa, Kuopiossa, Lohjalla, Mikkelissä, Mäntsälässä, Oulussa, Raahessa, Riihimäellä, Salossa ja Tampereella sekä Montessori-perusopetusta useilla paikkakunnilla.
      </p>
      <p>
        Montessori on Suomessa edelleen verrattain pieni osa koko kasvatusjärjestelmää. Sitä ei kannata liioitella. Mutta se ei myöskään ole marginaalinen internet-ilmiö. Sen ympärillä toimii päiväkoteja, kouluja, opettajia, perheitä, yhdistyksiä ja kansainvälistä opettajankoulutusta.
      </p>

      <h2>Ehkä kiinnostavinta on se, miksi vanhemmat etsivät vaihtoehtoja juuri nyt</h2>
      <p>
        Vuoden 2026 lapsuus näyttää hyvin erilaiselta kuin Maria Montessorin aikainen lapsuus. Tekoäly, älypuhelimet ja digitaaliset palvelut muuttavat lasten ympäristöä nopeasti. Samaan aikaan Montessori-pedagogiikan keskeiset ajatukset kuulostavat lähes päinvastaisilta: koske, kokeile, liiku, keskity, toista, tee itse.
      </p>
      <p>
        Ehkä juuri siksi yli sata vuotta vanha pedagogiikka herättää edelleen kiinnostusta. Se ei yritä ennustaa, millaista teknologiaa lapsi tarvitsee kahdenkymmenen vuoden päästä. Sen lähtökohta on paljon perustavampi: autetaan lasta kehittämään kyky oppia, kyky keskittyä, kyky ratkaista ongelmia, kyky huolehtia ympäristöstään ja vähitellen kyky pärjätä ilman, että aikuinen tekee kaiken hänen puolestaan.
      </p>
      <p>
        Maailma ympärillä muuttuu. Lapsen tarve päästä tekemään itse ei ole kadonnut mihinkään.
      </p>

      <h2>Vuoden 2026 Montessori-tapahtumia ja ajankohtia</h2>
      <ul>
        <li>2.–16.1.2026: haku Apollon Yhteiskoulun 7.–9.-luokkien Montessori-opetukseen, Helsinki.</li>
        <li>7.1.2026: Helsingin Montessori-koulun Teams-info vanhemmille.</li>
        <li>8.–20.1.2026: Espoon Montessori-opetuksen ensimmäisen luokan hakuaika Niittykummun ja Ruusutorpan kouluhin.</li>
        <li>3.–10.1., 16.–20.2. ja 1.6.–11.7.2026: AMI 3–6 Montessori-opettajakoulutuksen vuoden 2026 jaksot Helsingissä.</li>
        <li>14.3.2026 klo 9–12: Montessori-päiväkoti Aurinkoleijonan avoimet ovet Salossa ja Halikossa.</li>
        <li>1.–18.6. ja 13.–31.7.2026: AMI 6–12 Montessori-opettajakoulutuksen kesän lähiopetusjaksot Espoossa.</li>
        <li>Syksy 2026: AMI 6–12 -koulutus jatkuu etäluennoilla ja opetusharjoittelulla.</li>
        <li>Marras–joulukuu 2026: Helsingin Montessori-koulu on ilmoittanut järjestävänsä kaksi uutta infoiltaa ennen seuraavaa hakukierrosta; tarkat päivät julkaistaan myöhemmin.</li>
      </ul>

      <p>
        Jos haluat lukea lisää siitä, miten Montessori näkyy arjessa kotona, tutustu oppaaseen{" "}
        <Link to="/montessori-koulu/montessori-kotona">Montessori kotona</Link>. Menetelmän taustaa avaa puolestaan{" "}
        <Link to="/montessori-koulu/maria-montessori-elamakerta">Maria Montessorin elämäkerta</Link>.
      </p>
    </BlogPost>
  );
}
